import React, { useEffect, useState } from "react";

import Button from "components/Button";
import DebtorSearch from "components/DebtorSearchInput";
import Modal from "components/Modal";
import PaymentSpeedSelect from "components/PaymentSpeedInput/PaymentSpeedSelect";
import Select from "components/Select";
import { PERCENTAGE_LIST } from "constants/haulpay-broker";
import { FUNDING_REQUEST_CONTRACT_TYPES_MAP } from "helpers/constantTypes";
import type { IOption } from "models/Form";
import { ICompany } from "models/HaulPay";
import { LOCKED_BROKER_SPLIT_OPTION } from "features/main/HaulPay/Shared/paymentSettingsConfirmationFlow";
import type { SelfFinanceNoInvoiceToSelfFinanceWithInvoiceResult } from "features/main/HaulPay/Shared/selfFinanceNoInvoiceToSelfFinanceWithInvoiceFlow";

const LOCKED_BROKER_SPLIT =
  LOCKED_BROKER_SPLIT_OPTION ?? PERCENTAGE_LIST.find((p) => p.amount === 100) ?? PERCENTAGE_LIST[2];

export default function SelfFinanceNoInvoiceToSelfFinanceWithInvoiceModal({
  isOpen,
  closeModal,
  onConfirm,
  onCloseModal,
  fromContractType,
  toContractType,
  hasFactoringCompany = false,
  initialPaymentSpeed,
  initialBrokerSplit
}: Readonly<{
  isOpen: boolean;
  closeModal: () => void;
  onConfirm?: (result: SelfFinanceNoInvoiceToSelfFinanceWithInvoiceResult) => void;
  onCloseModal?: () => void;
  fromContractType?: string;
  toContractType?: string;
  hasFactoringCompany?: boolean;
  initialPaymentSpeed?: IOption | null;
  initialBrokerSplit?: IOption | null;
}>) {
  const [selectedDebtor, setSelectedDebtor] = useState<ICompany | null>(null);
  const [selectedPaymentSpeed, setSelectedPaymentSpeed] = useState<IOption | null>(
    initialPaymentSpeed ?? null
  );
  const [selectedBrokerSplit, setSelectedBrokerSplit] = useState<IOption>(
    hasFactoringCompany
      ? LOCKED_BROKER_SPLIT
      : initialBrokerSplit ?? (PERCENTAGE_LIST[0] as IOption)
  );

  useEffect(() => {
    setSelectedBrokerSplit(
      hasFactoringCompany
        ? LOCKED_BROKER_SPLIT
        : initialBrokerSplit ?? (PERCENTAGE_LIST[0] as IOption)
    );
  }, [hasFactoringCompany, initialBrokerSplit]);

  const fromLabel =
    FUNDING_REQUEST_CONTRACT_TYPES_MAP[
      fromContractType as keyof typeof FUNDING_REQUEST_CONTRACT_TYPES_MAP
    ] ?? fromContractType;
  const toLabel =
    FUNDING_REQUEST_CONTRACT_TYPES_MAP[
      toContractType as keyof typeof FUNDING_REQUEST_CONTRACT_TYPES_MAP
    ] ?? toContractType;

  const isConfirmDisabled = !selectedDebtor || !selectedPaymentSpeed;

  const handleCancel = () => {
    onCloseModal?.();
    closeModal();
  };

  const handleConfirm = () => {
    if (!selectedDebtor || !selectedPaymentSpeed) return;

    onConfirm?.({
      debtor: selectedDebtor,
      carrierPaymentSpeed: selectedPaymentSpeed,
      brokersQRFee: hasFactoringCompany ? (LOCKED_BROKER_SPLIT as IOption) : selectedBrokerSplit
    });
    closeModal();
  };

  return (
    <Modal
      isOpen={isOpen}
      closeModal={handleCancel}
      size="sm"
      wrapperClass="!my-auto desktop:!min-h-fit px-4 desktop:px-6"
      dialogClass="!bg-white dark:!bg-card !border !border-gray-200 dark:!border-card !p-0 !max-h-[calc(100vh-2rem)] !overflow-y-auto rounded-2xl shadow-lg w-full"
      closeClass="!hidden">
      <div className="border-b border-gray-200 dark:border-white/10 px-4 py-4 desktop:px-6 desktop:py-5">
        <p className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-500 dark:text-white/70">
          Contract Type Change
        </p>
        <h2 className="mt-2 text-lg font-semibold text-gray-900 dark:text-white desktop:text-xl">
          {fromLabel} &rarr; {toLabel}
        </h2>
      </div>

      <div className="flex flex-col gap-4 px-4 py-4 desktop:px-6 desktop:py-5">
        <p className="text-sm leading-relaxed text-gray-600 dark:text-white/75">
          This funding request does not have a debtor. Select a debtor and confirm the payment
          settings before changing the contract type to {toLabel}.
        </p>

        <div>
          <h6 className="mb-1.5 text-sm font-medium text-gray-900 dark:text-white">Debtor</h6>
          <DebtorSearch
            name="debtor"
            required
            isConnectedDebtor
            onSelect={(company) => setSelectedDebtor(company)}
            inputClass="!min-h-10 !rounded-lg !border !border-gray-200 dark:!border-white/10 !bg-white dark:!bg-card !px-3 !text-sm !text-gray-900 dark:!text-white"
            dropdownClass="!bg-white dark:!bg-card !border-gray-200 dark:!border-white/10"
            portalDropdown
          />
        </div>

        <div>
          <h6 className="mb-1.5 text-sm font-medium text-gray-900 dark:text-white">
            Payment speed
          </h6>
          <PaymentSpeedSelect
            name="self-finance-no-invoice-to-self-finance-with-invoice-payment-speed"
            noLabel
            inputProps={{
              value: selectedPaymentSpeed,
              onChange: (value: IOption) => setSelectedPaymentSpeed(value),
              inputClass:
                "!min-h-10 !rounded-lg !border !border-gray-200 dark:!border-white/10 !bg-white dark:!bg-card !px-3 !text-sm !text-gray-900 dark:!text-white",
              dropdownContainerClass: "!bg-white dark:!bg-card",
              direction: "down"
            }}
          />
          <p className="mt-1.5 text-xs text-gray-500 dark:text-white/60">
            Defaults to broker profile quick pay settings.
          </p>
        </div>

        <div>
          <h6 className="mb-1.5 text-sm font-medium text-gray-900 dark:text-white">Broker split</h6>
          <Select
            options={PERCENTAGE_LIST}
            value={hasFactoringCompany ? LOCKED_BROKER_SPLIT : selectedBrokerSplit}
            onChange={(value) => setSelectedBrokerSplit(value)}
            disabled={hasFactoringCompany}
            inputClass="!min-h-10 !rounded-lg !border !border-gray-200 dark:!border-white/10 !bg-white dark:!bg-card !px-3 !text-sm !text-gray-900 dark:!text-white"
            dropdownContainerClass="!bg-white dark:!bg-card"
            direction="up"
            usePortal={false}
          />
          {hasFactoringCompany && (
            <p className="mt-1.5 text-xs text-warning">
              Carrier has a factoring company on file and the broker split is locked to 100%.
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-col-reverse gap-2 border-t border-gray-200 dark:border-white/10 px-4 py-4 desktop:flex-row desktop:justify-end desktop:gap-3 desktop:px-6">
        <Button
          type="button"
          rounded
          variant="outline"
          onClick={handleCancel}
          className="!h-9 w-full text-sm desktop:w-auto desktop:min-w-[7rem] border-gray-300 dark:border-white/20 bg-transparent text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-white/5">
          Cancel
        </Button>
        <Button
          type="button"
          rounded
          onClick={handleConfirm}
          disabled={isConfirmDisabled}
          className="!h-9 w-full text-sm desktop:w-auto desktop:min-w-[10rem] !bg-primary dark:!bg-white !text-white dark:!text-card hover:!bg-primary/90 dark:hover:!bg-white/90">
          Confirm
        </Button>
      </div>
    </Modal>
  );
}
